import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import type { DefaultParams, TemporaryStep } from '../../steps.types';
import type { RtkErrorResponse } from '~/services/api/utils';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test } from '@bugbug/core/types/tests';
import type { Maybe } from '@bugbug/core/types/utils';
import { store, useAppDispatch } from '~/modules/store';
import { useCreateGroup } from '~/modules/tests/hooks/useCreateGroup/useCreateGroup';
import { addStepsToCurrentTestGroup, updateCurrentTestQuery } from '~/modules/tests/tests.api';
import { showStepError } from '~/modules/tests/tests.utils';
import { UIStateActions } from '~/modules/uiState/uiState.redux';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { useGetStepsDefaultParamsQuery } from '../../steps.api';
import { StepsActions } from '../../steps.redux';
import { getInitialValues, pickDefaultStepParams } from '../../steps.utils';

type CreateTemporaryStep = <TStep extends Step>(params: {
  testId: Test['id'];
  groupId: Group['id'];
  stepType: TStep['type'];
  atIndex: number;
  stepInitialParams?: Partial<TStep>;
}) => Promise<Maybe<TemporaryStep>>;

export const useCreateTemporaryStep = () => {
  const dispatch = useAppDispatch();
  const [createGroup, createGroupState] = useCreateGroup();
  const { currentData: groupedDefaultStepParams } = useGetStepsDefaultParamsQuery();

  const handleCreateStep = useCallback<CreateTemporaryStep>(
    async ({ testId, groupId, stepType, atIndex, stepInitialParams }) => {
      try {
        if (!groupedDefaultStepParams) throw new Error('Default step params are not available');

        const toExistingGroup = !!groupId;
        if (!toExistingGroup) {
          // When test is empty, create a new group
          const result = await createGroup({ testId, name: null, stepsIds: [], atIndex: 0 });
          groupId = result.id;
        }

        const defaultParamsForThisStep = pickDefaultStepParams(
          stepType,
          groupedDefaultStepParams,
          stepInitialParams as DefaultParams,
        );
        const temporaryStep = {
          ...getInitialValues({
            ...stepInitialParams,
            type: stepType,
          }),
          ...defaultParamsForThisStep,
          id: uuid(),
          type: stepType,
          groupId,
          isTemporary: true,
          atIndex,
        } as TemporaryStep;

        updateCurrentTestQuery(store.getState, dispatch, (draft) => {
          addStepsToCurrentTestGroup(draft, { groupId, steps: [temporaryStep], atIndex });
        });

        dispatch(StepsActions.setCurrentStep(temporaryStep));
        dispatch(UIStateActions.addRecentlyAddedStepsIds({ stepsIds: [temporaryStep.id] }));

        return temporaryStep;
      } catch (error) {
        showStepError(ACTIONS.CREATE, error as Error | RtkErrorResponse);
      }

      return null;
    },
    [createGroup, dispatch, groupedDefaultStepParams],
  );

  return useMemo(
    () => [handleCreateStep, createGroupState] as const,
    [handleCreateStep, createGroupState],
  );
};
